import { jsPDF } from "jspdf";

const sanitizeDisclosureFileName = (title: string) =>
  `${title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}.pdf`;

type DisclosurePdfBlock = {
  type: "h2" | "h3" | "p" | "li";
  text: string;
};

export const downloadDisclosurePdf = async (title: string, html: string) => {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  const blocks = Array.from(
    wrapper.querySelectorAll("h2, h3, p, li"),
  ).reduce<DisclosurePdfBlock[]>((acc, node) => {
    if (node.closest("li") && node.tagName.toLowerCase() === "p") {
      return acc;
    }

    const text = node.textContent?.replace(/\s+/g, " ").trim();
    if (!text) return acc;

    acc.push({
      type: node.tagName.toLowerCase() as DisclosurePdfBlock["type"],
      text,
    });
    return acc;
  }, []);

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 42;
  const contentX = margin + 18;
  const contentWidth = pageWidth - margin * 2 - 36;
  const pageBottom = pageHeight - margin;
  let y = margin;

  const setTextColor = (hex: string) => {
    const value = hex.replace("#", "");
    pdf.setTextColor(
      parseInt(value.slice(0, 2), 16),
      parseInt(value.slice(2, 4), 16),
      parseInt(value.slice(4, 6), 16),
    );
  };

  const addPage = () => {
    pdf.addPage();
    y = margin;
  };

  const ensureSpace = (height: number) => {
    if (y + height > pageBottom) addPage();
  };

  const drawWrappedText = ({
    text,
    x,
    maxWidth,
    fontSize,
    lineHeight,
    fontStyle = "normal",
    color = "#334155",
  }: {
    text: string;
    x: number;
    maxWidth: number;
    fontSize: number;
    lineHeight: number;
    fontStyle?: "normal" | "bold";
    color?: string;
  }) => {
    pdf.setFont("helvetica", fontStyle);
    pdf.setFontSize(fontSize);
    setTextColor(color);

    const lines = pdf.splitTextToSize(text, maxWidth) as string[];
    lines.forEach((line) => {
      if (y + lineHeight > pageBottom) addPage();
      pdf.text(line, x, y);
      y += lineHeight;
    });
  };

  const drawBlock = (block: DisclosurePdfBlock) => {
    if (block.type === "h2") {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      const lines = pdf.splitTextToSize(block.text, contentWidth) as string[];
      ensureSpace(lines.length * 19 + 14);
      y += 12;
      drawWrappedText({
        text: block.text,
        x: contentX,
        maxWidth: contentWidth,
        fontSize: 14,
        lineHeight: 19,
        fontStyle: "bold",
        color: "#0f172a",
      });
      y += 3;
      return;
    }

    if (block.type === "h3") {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      const lines = pdf.splitTextToSize(block.text, contentWidth) as string[];
      ensureSpace(lines.length * 17 + 12);
      y += 10;
      drawWrappedText({
        text: block.text,
        x: contentX,
        maxWidth: contentWidth,
        fontSize: 12,
        lineHeight: 17,
        fontStyle: "bold",
        color: "#1e293b",
      });
      y += 2;
      return;
    }

    if (block.type === "li") {
      const bulletX = contentX + 10;
      const textX = contentX + 25;
      const liWidth = contentWidth - 25;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10.5);
      const lines = pdf.splitTextToSize(block.text, liWidth) as string[];
      ensureSpace(lines.length * 15 + 5);
      setTextColor("#334155");
      pdf.text("•", bulletX, y);
      drawWrappedText({
        text: block.text,
        x: textX,
        maxWidth: liWidth,
        fontSize: 10.5,
        lineHeight: 15,
        color: "#334155",
      });
      y += 4;
      return;
    }

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10.5);
    const lines = pdf.splitTextToSize(block.text, contentWidth) as string[];
    ensureSpace(lines.length * 15 + 9);
    drawWrappedText({
      text: block.text,
      x: contentX,
      maxWidth: contentWidth,
      fontSize: 10.5,
      lineHeight: 15,
      color: "#334155",
    });
    y += 8;
  };

  const headerY = y;
  pdf.setDrawColor(226, 232, 240);
  pdf.setFillColor(248, 250, 252);
  pdf.roundedRect(margin, headerY, pageWidth - margin * 2, 78, 10, 10, "FD");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  setTextColor("#64748b");
  pdf.text("DISCLOSURE DOCUMENT", contentX, headerY + 22);
  y = headerY + 45;
  drawWrappedText({
    text: title,
    x: contentX,
    maxWidth: contentWidth,
    fontSize: 15,
    lineHeight: 20,
    fontStyle: "bold",
    color: "#0f172a",
  });
  y = headerY + 100;

  blocks.forEach(drawBlock);
  pdf.save(sanitizeDisclosureFileName(title));
};
